import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { Lock as LockIcon, AutoAwesome, WbSunny } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import NorthIndianChart from '../components/charts/NorthIndianChart';
import AnalysisTabBar from '../components/AnalysisTabBar';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import { inlineLocale, translateInline } from '../i18n/inlineTranslations';
import { normalizeLanguage } from '../i18n/languages';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, index) => currentYear + index);

const getSummary = (report: any) => report?.yearlySummary || {};
const RESULT_SECTIONS = [
  { key: 'overall', title: 'Overall Year Theme', titleHi: 'कुल वर्ष थीम' },
  { key: 'career', title: 'Career & Job', titleHi: 'करियर और नौकरी' },
  { key: 'business', title: 'Business', titleHi: 'बिजनेस' },
  { key: 'finance', title: 'Finance & Savings', titleHi: 'धन और बचत' },
  { key: 'relationshipMarriage', title: 'Relationship & Marriage', titleHi: 'रिश्ते और विवाह' },
  { key: 'family', title: 'Family Matters', titleHi: 'परिवार' },
  { key: 'children', title: 'Children', titleHi: 'संतान' },
  { key: 'education', title: 'Education', titleHi: 'शिक्षा' },
  { key: 'propertyVehicle', title: 'Property & Vehicle', titleHi: 'प्रॉपर्टी और वाहन' },
  { key: 'travelForeign', title: 'Foreign Travel / Relocation', titleHi: 'विदेश यात्रा / रीलोकेशन' },
  { key: 'healthWellness', title: 'Health & Wellness', titleHi: 'स्वास्थ्य और वेलनेस' },
  { key: 'spirituality', title: 'Spiritual Growth', titleHi: 'आध्यात्मिक विकास' },
  { key: 'legalCompetition', title: 'Legal / Competition / Enemies', titleHi: 'कानूनी / प्रतियोगिता / विरोधी' },
  { key: 'statusRecognition', title: 'Social Status & Recognition', titleHi: 'मान-सम्मान और पहचान' },
] as const;

const labelText = (language: string, en: string, hi: string) => translateInline(language, en, hi);
const formatRangeDate = (value: string | Date, language: string) => (
  value ? new Date(value).toLocaleDateString(inlineLocale(language), { day: '2-digit', month: 'short', year: 'numeric' }) : '--'
);

const TajikaVarshphal: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { i18n } = useTranslation();
  const isPremium = hasPremiumSubscription(authState.user);
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const [targetYear, setTargetYear] = useState(currentYear);
  const [report, setReport] = useState<any>(null);
  const [charts, setCharts] = useState<any[]>([]);
  const [selectedChartId, setSelectedChartId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPremiumLock, setShowPremiumLock] = useState(false);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess({
    onShowPremiumLock: () => setShowPremiumLock(true)
  });

  const summary = useMemo(() => getSummary(report), [report]);

  const generateReport = async () => {
    const chartId = id || selectedChartId;
    if (!chartId) {
      setError('Please select a saved chart first.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await checkFeatureAccess({
        featureKey: 'VARSHPHAL',
        apiCall: (trialAccessToken?: string) => axios.post('/varshaphal/report', {
          chartId,
          targetYear,
          language
        }, {
          headers: trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : undefined
        }),
        onSuccess: (payload: any) => {
          setShowPremiumLock(false);
          setReport(payload.data);
        }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Unable to generate Tajika Varshphal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPremium && (id || selectedChartId)) {
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedChartId, isPremium, language]);

  useEffect(() => {
    if (id || !authState.token) return;
    fetch(`${API_BASE_URL}/charts/my-charts?page=1&limit=50&type=individual`, {
      headers: { Authorization: `Bearer ${authState.token}` }
    })
      .then((response) => response.json())
      .then((payload) => {
        const items = payload?.data?.charts || [];
        setCharts(items);
        if (items[0]?._id) setSelectedChartId(items[0]._id);
      })
      .catch(() => setCharts([]));
  }, [id, authState.token]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {id && <AnalysisTabBar chartId={id} active="varshphal" />}

      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <Chip icon={<WbSunny />} label={labelText(language, 'Studio Pro yearly module', 'स्टूडियो प्रो वार्षिक मॉड्यूल')} color="success" sx={{ mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                {labelText(language, 'Tajika Varshphal', 'ताजिक वर्षफल')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                {labelText(
                  language,
                  'Solar Return based yearly horoscope with Varsha Lagna, Muntha, Varsha Lord, Sahams, monthly guidance, remedies, and practical life themes personalised from this saved Kundli.',
                  'सोलर रिटर्न आधारित वार्षिक राशिफल जिसमें वर्ष लग्न, मुंथा, वर्षेश, सहम, मासिक मार्गदर्शन, उपाय और इस कुंडली के अनुसार व्यक्तिगत जीवन थीम शामिल हैं।'
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {!id && (
                <TextField
                  select
                  label={labelText(language, 'Saved chart', 'सेव कुंडली')}
                  value={selectedChartId}
                  size="small"
                  onChange={(event) => setSelectedChartId(event.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {charts.map((chart) => (
                    <MenuItem key={chart._id} value={chart._id}>{chart.birthDetails?.name || labelText(language, 'Untitled Chart', 'बिना नाम की कुंडली')}</MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                select
                label={labelText(language, 'Year', 'वर्ष')}
                value={targetYear}
                size="small"
                onChange={(event) => setTargetYear(Number(event.target.value))}
                sx={{ width: 120 }}
              >
                {YEAR_OPTIONS.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
              <Button variant="contained" startIcon={<AutoAwesome />} onClick={generateReport} disabled={loading || trialLoading}>
                {labelText(language, 'Generate', 'बनाएं')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {showPremiumLock && (
        <Alert
          severity="info"
          icon={<LockIcon />}
          action={<Button variant="contained" onClick={() => navigate('/upgrade')}>{labelText(language, 'Upgrade', 'अपग्रेड')}</Button>}
          sx={{ mb: 3 }}
        >
          {labelText(
            language,
            'Your free Studio Pro access is used. Choose Studio Pro for continued access.',
            'आपका निःशुल्क स्टूडियो प्रो एक्सेस उपयोग हो चुका है। आगे उपयोग के लिए स्टूडियो प्रो चुनें।'
          )}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {report && !loading && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{labelText(language, 'Core Year Markers', 'मुख्य वर्ष संकेतक')}</Typography>
                <List dense>
                  <ListItem><ListItemText primary={labelText(language, 'Solar Return', 'सोलर रिटर्न')} secondary={new Date(report.solarReturnDate).toLocaleString(inlineLocale(language))} /></ListItem>
                  <ListItem><ListItemText primary={labelText(language, 'Varsha Lagna', 'वर्ष लग्न')} secondary={`${report.varshaLagna?.sign || '--'} ${Number(report.varshaLagna?.degreeInSign || 0).toFixed(2)}°`} /></ListItem>
                  <ListItem><ListItemText primary={labelText(language, 'Muntha (Natal Lagna)', 'मुंथा (जन्म लग्न)')} secondary={`${report.muntha?.sign || '--'} ${labelText(language, 'in House', 'भाव')} ${report.muntha?.natalHouse ?? report.muntha?.house ?? '--'}`} /></ListItem>
                  <ListItem><ListItemText primary={labelText(language, 'Muntha in Varsha Chart', 'वर्ष कुंडली में मुंथा')} secondary={`${labelText(language, 'House', 'भाव')} ${report.muntha?.varshaHouse ?? '--'}`} /></ListItem>
                  <ListItem><ListItemText primary={labelText(language, 'Muntha Lord', 'मुंथा स्वामी')} secondary={report.muntha?.lord || '--'} /></ListItem>
                  <ListItem><ListItemText primary={labelText(language, 'Varsha Lord', 'वर्षेश')} secondary={report.varshaLord || '--'} /></ListItem>
                  <ListItem><ListItemText primary={labelText(language, 'Varshesh Method', 'वर्षेश पद्धति')} secondary={report.varshaLordDetails?.method || '--'} /></ListItem>
                </List>
                {report.varshaChart && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <NorthIndianChart
                      chartData={report.varshaChart}
                      size={320}
                      showHouseLabels
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{labelText(language, 'Yearly Summary', 'वार्षिक सारांश')}</Typography>
                <Typography sx={{ mt: 1 }}>{summary.overall}</Typography>
                <Typography sx={{ mt: 1 }}>{summary.finalAdvice}</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {RESULT_SECTIONS.map((section) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={section.key}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography fontWeight={800}>{labelText(language, section.title, section.titleHi)}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{summary[section.key] || labelText(language, 'Not available yet.', 'अभी उपलब्ध नहीं है।')}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {labelText(language, 'Varshesh Candidates', 'वर्षेश उम्मीदवार')}
                </Typography>
                <List dense>
                  {(report.varshaLordDetails?.candidates || []).map((candidate: any) => (
                    <ListItem key={candidate.planet} alignItems="flex-start">
                      <ListItemText
                        primary={`${candidate.planet} - ${labelText(language, 'Score', 'स्कोर')} ${candidate.score}`}
                        secondary={[
                          candidate.roles?.join(', '),
                          candidate.sign ? `${candidate.sign}${candidate.house ? `, ${labelText(language, 'House', 'भाव')} ${candidate.house}` : ''}` : '',
                          candidate.notes?.slice(0, 2).join(' '),
                        ].filter(Boolean).join(' | ')}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {labelText(language, 'Important Sahams', 'मुख्य सहम')}
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  {Object.entries(report.sahams || {}).slice(0, 12).map(([name, saham]: any) => (
                    <Grid size={{ xs: 6, sm: 4 }} key={name}>
                      <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                        <Typography variant="body2" fontWeight={800} sx={{ textTransform: 'capitalize' }}>{name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {saham?.sign || '--'} {Number(saham?.degreeInSign || 0).toFixed(2)}°
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {labelText(language, 'Tajika Yogas', 'ताजिक योग')}
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  {(report.tajikaYogas || []).length ? report.tajikaYogas.map((yoga: any, index: number) => (
                    <Grid size={{ xs: 12, md: 6 }} key={`${yoga.name}-${index}`}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Chip size="small" label={yoga.name} color={yoga.type?.includes('obstruction') ? 'warning' : 'success'} />
                          {yoga.strength && <Chip size="small" label={yoga.strength} variant="outlined" />}
                        </Box>
                        <Typography variant="body2" fontWeight={800}>{(yoga.planets || []).join(' - ')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{yoga.interpretation}</Typography>
                      </Paper>
                    </Grid>
                  )) : (
                    <Grid size={12}>
                      <Typography variant="body2" color="text.secondary">
                        {labelText(language, 'No major orb-based Tajika yoga detected in the current Varsha chart.', 'वर्तमान वर्ष कुंडली में कोई प्रमुख orb-based ताजिक योग नहीं मिला।')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {labelText(language, 'Mudda Dasha Timeline', 'मुद्दा दशा टाइमलाइन')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {report.muddaDasha?.basis || ''}
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  {(report.muddaDasha?.periods || []).map((period: any) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${period.lord}-${period.startDate}`}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography fontWeight={800}>{period.lord}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatRangeDate(period.startDate, language)} - {formatRangeDate(period.endDate, language)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.75 }}>{period.theme}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{labelText(language, 'Monthly Timeline', 'मासिक टाइमलाइन')}</Typography>
                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  {(summary.monthlyTimeline || []).map((month: any) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={month.month}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography fontWeight={800}>{month.month}</Typography>
                        <Chip size="small" label={`${labelText(language, 'House', 'भाव')} ${month.activatedHouse || '--'}`} sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary">{month.theme}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.75 }}>{month.advice}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card><CardContent><Typography variant="h6" sx={{ fontWeight: 800 }}>{labelText(language, 'Risk Areas', 'सावधानी क्षेत्र')}</Typography><List dense>{(summary.riskAreas || []).map((item: string) => <ListItem key={item}><ListItemText primary={item} /></ListItem>)}</List></CardContent></Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card><CardContent><Typography variant="h6" sx={{ fontWeight: 800 }}>{labelText(language, 'Favourable Periods', 'शुभ अवधि')}</Typography><List dense>{(summary.favourablePeriods || []).map((item: any) => <ListItem key={item.month || item.period}><ListItemText primary={item.month || item.period} secondary={item.reason} /></ListItem>)}</List></CardContent></Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card><CardContent><Typography variant="h6" sx={{ fontWeight: 800 }}>{labelText(language, 'Challenging Periods', 'चुनौतीपूर्ण अवधि')}</Typography><List dense>{(summary.challengingPeriods || []).map((item: any) => <ListItem key={item.month || item.period}><ListItemText primary={item.month || item.period} secondary={item.reason} /></ListItem>)}</List></CardContent></Card>
          </Grid>

          <Grid size={12}>
            <Card><CardContent><Typography variant="h6" sx={{ fontWeight: 800 }}>{labelText(language, 'Remedies & Final Advice', 'उपाय और अंतिम सलाह')}</Typography><List dense>{(summary.remedies || []).map((item: string) => <ListItem key={item}><ListItemText primary={item} /></ListItem>)}</List><Typography sx={{ mt: 1 }}>{summary.finalAdvice}</Typography></CardContent></Card>
          </Grid>

          <Grid size={12}>
            <Alert severity="warning">
              {labelText(
                language,
                'Tajika Varshphal gives interpretive yearly guidance. It should not be treated as medical, legal, financial, or certain prediction advice.',
                'ताजिक वर्षफल व्याख्यात्मक वार्षिक मार्गदर्शन देता है। इसे चिकित्सा, कानूनी, वित्तीय या निश्चित भविष्यवाणी सलाह न मानें।'
              )}
            </Alert>
          </Grid>
        </Grid>
      )}

      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading}
        onCancel={closePopup}
        onContinue={popup.nextAction}
      />
    </Container>
  );
};

export default TajikaVarshphal;
